import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Star } from "lucide-react";
import { ContactButtons, CALL_URL, PHONE_DISPLAY } from "./ContactButtons";

const HERO_IMG =
  "https://images.unsplash.com/photo-1673419229354-284ca2e29e4c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwyfHx2YXJhbmFzaSUyMGdhbmdlcyUyMHN1bnJpc2V8ZW58MHx8fHwxNzg2MTI2MDU5fDA&ixlib=rb-4.1.0&q=85";

const LINES = ["A Sanctuary", "in the Sacred", "City"];

const lineVariants = {
  hidden: { y: "115%" },
  visible: (i) => ({
    y: "0%",
    transition: { delay: 0.35 + i * 0.14, duration: 1.1, ease: [0.16, 1, 0.3, 1] },
  }),
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative flex h-screen min-h-[640px] items-end overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110">
        <img
          src={HERO_IMG}
          alt="Sunrise over the Ganges, Varanasi"
          className="h-full w-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-[#080809] via-[#080809]/55 to-[#080809]/30" />

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-24 pt-28 md:px-10 md:pb-28 md:pt-32">
        <motion.p
          data-testid="hero-overline"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 1 }}
          className="text-glow mb-6 text-[11px] font-medium uppercase tracking-[0.4em] text-[#C5A059] md:text-xs"
        >
          Pipewalas House &amp; Homestays — Thatheri Bazar, Kashi
        </motion.p>

        <h1 data-testid="hero-title" className="text-glow font-serif text-[12.8vw] leading-[0.92] tracking-tight text-[#F5F5F0] sm:text-[10.4vw] lg:text-[6.8rem]">
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                custom={i}
                variants={lineVariants}
                initial="hidden"
                animate="visible"
                className="block"
              >
                {i === 1 ? (
                  <>
                    in the <em className="font-light italic text-[#C5A059]">Sacred</em>
                  </>
                ) : i === 2 ? (
                  <>City</>
                ) : (
                  line
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 flex flex-col gap-8 md:flex-row md:items-end md:justify-between"
        >
          <p data-testid="hero-subtitle" className="text-glow max-w-md text-base leading-relaxed text-[#F5F5F0]/85 md:text-lg">
            An honest, affordable homestay tucked into the metalworkers&rsquo;
            bazaar of old Varanasi — clean rooms, fair prices, and the ghats a
            short walk away.
          </p>
          <div className="flex flex-col items-start gap-5">
            <div data-testid="hero-rating" className="flex items-center gap-2 text-sm text-[#F5F5F0]/80">
              <Star className="h-4 w-4 fill-[#C5A059] text-[#C5A059]" />
              <span className="font-semibold text-[#F5F5F0]">4.4</span>
              <span>on Google</span>
            </div>
            <ContactButtons idPrefix="hero" />
            <a
              data-testid="hero-phone-link"
              href={CALL_URL}
              className="text-glow text-sm font-medium text-[#F5F5F0]/75 transition-colors hover:text-[#C5A059]"
            >
              or call {PHONE_DISPLAY} directly
            </a>
          </div>
        </motion.div>
      </motion.div>

      <motion.a
        data-testid="hero-scroll-cue"
        href="#about"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-6 left-1/2 z-10 -translate-x-1/2 text-[#C5A059]/70"
        aria-label="Scroll down"
      >
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }} className="block">
          <ArrowDown className="h-5 w-5" />
        </motion.span>
      </motion.a>
    </section>
  );
};
